import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';
import { State, StateChangeHistory } from '../../types/asset.types';
import { ApiEndpoints } from '../../services/ApiUrls';

// Pagination interface for states
export interface StatePaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: State[];
}

// Define types for request and response
export interface StateCreateInput {
  name: string;
  code: string;
  country: string;
  isActive?: boolean;
}

export interface StateUpdateInput extends Partial<StateCreateInput> {
  updateReason?: string;
}

export interface StateFilter {
  search?: string;
  countryId?: string;
  isActive?: boolean;
  dropdown?: boolean;
}

// State history data interface
export interface StateHistoryData {
  id: string;
  name: string;
  changes: StateChangeHistory[];
}

// Create API
export const stateApi = createApi({
  reducerPath: 'stateApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['State', 'StateHistory'],
  endpoints: (builder) => ({
    getStates: builder.query<StatePaginatedResponse, StateFilter | void>({
      query: (filter = {}) => {
        const params = new URLSearchParams();
        params.append('paginate', 'false');
        if (filter && 'dropdown' in filter && filter.dropdown) {
          params.append('dropdown', 'true');
        }
        if (filter) {
          if ('search' in filter && filter.search)
            params.append('search', filter.search);
          if ('countryId' in filter && filter.countryId)
            params.append('country', filter.countryId);
          if ('isActive' in filter && filter.isActive !== undefined)
            params.append('is_active', filter.isActive.toString());
        }
        return { url: ApiEndpoints.states.list, params };
      },
      providesTags: ['State'],
    }),
    getStateById: builder.query<State, string>({
      query: (id) => ApiEndpoints.states.detail(id),
      providesTags: (result, error, id) => [{ type: 'State', id }],
    }),
    createState: builder.mutation<State, StateCreateInput>({
      query: (state) => ({
        url: ApiEndpoints.states.list,
        method: 'POST',
        body: state,
      }),
      invalidatesTags: ['State'],
    }),
    updateState: builder.mutation<
      State,
      { id: string; state: StateUpdateInput }
    >({
      query: ({ id, state }) => ({
        url: ApiEndpoints.states.detail(id),
        method: 'PATCH',
        body: state,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'State', id },
        'State',
      ],
    }),
    getStateHistory: builder.query<StateHistoryData, string>({
      query: (id) => ApiEndpoints.states.history(id),
      providesTags: (result, error, id) => [{ type: 'StateHistory', id }],
    }),
  }),
});

// Export hooks
export const {
  useGetStatesQuery,
  useGetStateByIdQuery,
  useCreateStateMutation,
  useUpdateStateMutation,
  useGetStateHistoryQuery,
} = stateApi;
